import { ShipmentStatusLabelProps } from '../ShipmentStatusLabel/ShipmentStatusLabel';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { ProductInformationProps, TrackingProductAlert } from '../ProductInformation/ProductInformationCard';
import {
  AlertProps,
  BaseContentSection,
  ShipmentStatus,
  OrderShipment,
  ProgressStatus,
  ShipmentHistoryItem,
  StoreInformationType,
  TrackingOrder,
  ShipmentLineItem,
} from '@jbhi-fi/apis/src/orderTrackingThemeApi/types';
import { VerticalTimelineContent } from '@jbhi-fi/lanyard-ui/components/VerticalTimeline/VerticalTimeline';
import { format, isValid } from 'date-fns';
import { getFriendlyFormattedDate, getTimeWindow } from '@jbhi-fi/utils/src/date/getFriendlyFormattedDate';
import { AlgoliaStoreData } from '@jbhi-fi/apis/src/algolia/types/AlgoliaStoreData';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { getTwelveHourTimeFromString } from '@jbhi-fi/utils/src/date/getTwelveHourTimeFromString';
import { getStoreFinderUrl } from '@jbhi-fi/utils/src/store/getStoreFinderUrl';
import { toZonedTime } from 'date-fns-tz';
import { MarketplaceSellerCalloutProps } from '../../Marketplace/MarketplaceSellerCallout/MarketplaceSellerCallout';

export abstract class OrderShipmentTransformer {
  static toShipmentStatusProps = (shipment: OrderShipment): ShipmentStatusLabelProps => {
    const etaDate = new Date(shipment.shipmentEta || '');
    const formattedEta = isValid(etaDate) ? format(etaDate, 'd MMM') : 'unknown';
    switch (shipment.status) {
      case 'Delivered':
        return {
          label: shipment.type === 'DigitalDelivery' ? 'Delivered by email' : 'Delivered',
          icon: 'Tick',
          color: palette.success.main,
        };
      case 'Collected':
        return { label: 'Collected', icon: 'Tick', color: palette.success.main };
      case 'Cancelled':
        return { label: 'Cancelled', icon: 'Error', color: palette.warning.main };
      default:
        return { label: `${shipment.type === 'CustomerCollect' ? 'From' : 'ETA'} ${formattedEta}` };
    }
  };

  static toProgressStatus = (status: string): ProgressStatus => {
    switch (status) {
      case 'OutForDelivery':
        return 'Out for delivery';
      case 'Shipped':
        return 'In transit';
      case 'AwaitingCollection':
        return 'Ready to collect';
      case 'AwaitingUberPickup':
        return 'Awaiting Uber pickup';
      default:
        return status as ProgressStatus;
    }
  };

  static toShipmentHistory = (
    shipmentConfirmedAt: string,
    shipmentHistory?: Array<ShipmentHistoryItem>
  ): VerticalTimelineContent => {
    const groupedItems: VerticalTimelineContent = [];
    if (!shipmentHistory?.length)
      return [
        {
          heading: 'Confirmed',
          content: [
            {
              leftText: 'Order confirmed',
              rightText: getFriendlyFormattedDate(shipmentConfirmedAt, 'long', true),
            },
          ],
        },
      ];

    shipmentHistory.forEach(item => {
      const { shipmentStatus, status, timestamp } = item;

      const mappedStatus = this.toProgressStatus(shipmentStatus) || shipmentStatus;
      const formattedDate = getFriendlyFormattedDate(timestamp, 'long', true);

      let group = groupedItems.find(
        group => group.heading === mappedStatus && group.content[0].rightText === formattedDate
      );

      if (!group) {
        group = {
          heading: mappedStatus,
          content: [],
        };
        groupedItems.push(group);
      }

      group.content.push({
        leftText: status,
        rightText: formattedDate,
      });
    });

    groupedItems.push({
      heading: 'Confirmed',
      content: [
        {
          leftText: 'Order confirmed',
          rightText: getFriendlyFormattedDate(shipmentConfirmedAt, 'long', true),
        },
      ],
    });

    return groupedItems;
  };

  private static getProductAlert = (
    isPreOrder: boolean,
    preOrderReleaseDate?: string,
    isCancelled?: boolean,
    cancellationReason?: string
  ): TrackingProductAlert | undefined => {
    if (isCancelled)
      return {
        type: 'Error',
        content: cancellationReason
          ? `**Cancelled and refunded:** ${cancellationReason}`
          : '**Cancelled and refunded**',
      };

    if (isPreOrder)
      return {
        type: 'Info',
        content: `${
          preOrderReleaseDate
            ? `**Pre-order release date ${getFriendlyFormattedDate(preOrderReleaseDate, 'short')}**`
            : '**Pre-order**'
        }`,
      };

    return undefined;
  };

  static toProductInformationCardsProps = (
    shipmentProducts: OrderShipment['lineItems'],
    { isDigital, isCancelled }: { isDigital?: boolean; isCancelled?: boolean } = {}
  ): Array<ProductInformationProps> => {
    return shipmentProducts.map(lineItem => {
      return {
        product: {
          name: lineItem.description,
          ...lineItem,
        },
        marketplaceSeller: this.toMarketplaceSellerCalloutProps(lineItem),
        note: isDigital ? 'Code emailed separately' : undefined,
        alert: this.getProductAlert(
          lineItem.isPreOrder,
          lineItem.preOrderReleaseDate,
          isCancelled,
          lineItem.cancellationReason
        ),
      };
    });
  };

  static toShipmentEta = (shipment: OrderShipment) => {
    return shipment.messaging?.carrier?.name.includes('Uber')
      ? this.toUberDeliveryDate(shipment.messaging.carrier.name, shipment.shipmentEta)
      : getFriendlyFormattedDate(shipment.shipmentEta);
  };

  static toFriendlyShipmentStatus = (shipmentStatus: ShipmentStatus) => {
    switch (shipmentStatus) {
      case 'Confirmed':
        return 'Order confirmed';
      case 'AwaitingCollection':
        return 'Awaiting collection';
      case 'Packed':
        return 'Packed and ready for carrier pickup';
      case 'AwaitingUberPickup':
        return 'Awaiting Uber pickup';
      case 'OutForDelivery':
        return 'Out for delivery';
      default:
        return shipmentStatus;
    }
  };

  private static isStoreOpen = (closingTimeString?: string, timeZoneId: string = 'Australia/Victoria') => {
    if (!closingTimeString) return false;
    const [hours, minutes] = closingTimeString.split(':');
    const currentDay = new Date();
    const zonedDate = toZonedTime(currentDay, timeZoneId);

    const closingTime = new Date();
    closingTime.setHours(Number(hours), Number(minutes));

    const timeDifference = closingTime.getTime() - zonedDate.getTime();
    return timeDifference >= 30 * 60 * 1000;
  };

  private static getStoreTradingHours = (
    scheduledTradingHoursForTheComingWeek: Array<{
      closingTime: string;
      dayOfWeek: number;
      isOpen: boolean;
      openingTime: string;
      date: string;
    }>,
    timezoneId?: string
  ): Array<string> => {
    const closedText = 'Closed';
    const currentDay = new Date().getDay();
    const tomorrow = (currentDay + 1) % 7;
    let tradingHours = ['Contact store'];
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    if (scheduledTradingHoursForTheComingWeek.length) {
      tradingHours = scheduledTradingHoursForTheComingWeek.map(schedule => {
        const openingTime = getTwelveHourTimeFromString(schedule.openingTime);
        const closingTime = getTwelveHourTimeFromString(schedule.closingTime);
        if (schedule.dayOfWeek === currentDay) {
          return schedule.isOpen && this.isStoreOpen(schedule.closingTime, timezoneId)
            ? `Open today till ${closingTime}`
            : `Today ${closedText}`;
        } else if (schedule.dayOfWeek === tomorrow) {
          return schedule?.isOpen ? `Tomorrow ${openingTime} - ${closingTime}` : `Tomorrow ${closedText}`;
        }
        return `${daysOfWeek[schedule.dayOfWeek]} ${openingTime} - ${closingTime}`;
      });
    }
    return tradingHours;
  };

  private static mapStoreInformation = (store?: AlgoliaStoreData): StoreInformationType | null => {
    if (!store) return null;
    return {
      name: store.storeName,
      addressLine1: store.storeAddress.Line1,
      addressLine2: store.storeAddress.Line2,
      suburb: store.storeAddress.Suburb,
      state: store.storeAddress.State,
      postcode: store.storeAddress.Postcode,
      openingHours: this.getStoreTradingHours(
        toCamelCase(store.scheduledTradingHoursForTheComingWeek),
        store.timeZoneId
      ),
      isOpen: this.isStoreOpen(
        store?.scheduledTradingHoursForTheComingWeek?.find(hours => hours.DayOfWeek === new Date().getDay())
          ?.ClosingTime,
        store.timeZoneId
      ),
      directionsLink: getStoreFinderUrl(store.storeName),
    } as StoreInformationType;
  };

  static toUberDeliveryDate = (carrierName: string, shipmentEta: string | null) => {
    if (carrierName === 'Uber ASAP') {
      return getFriendlyFormattedDate(shipmentEta, 'friendly', true);
    }
    return getTimeWindow(shipmentEta);
  };

  static toShipmentInformation = (
    shipments: Array<OrderShipment>,
    stores?: Array<AlgoliaStoreData>
  ): Array<OrderShipment> => {
    return shipments.map(shipment => {
      return {
        ...shipment,
        statusUpdatedAt: getFriendlyFormattedDate(shipment.statusUpdatedAt, 'friendly', true),
        progressStatus: this.toProgressStatus(shipment.status),
        messaging: {
          ...shipment.messaging,
          store: this.mapStoreInformation(stores?.find(store => store.objectID === shipment.messaging?.locationId)),
        },
      } as OrderShipment;
    });
  };

  static parseContentSections = (order: TrackingOrder): TrackingOrder => {
    const parseShipment = (shipment: OrderShipment): OrderShipment => {
      const parseMarkdownString = (str?: string) =>
        str?.replace(
          /{{dateExpected}}/g,
          getFriendlyFormattedDate(shipment.lineItems?.[0]?.dateExpected || '', 'friendly')
        );

      const parseBaseContentSection = (section?: BaseContentSection) =>
        section
          ? {
              ...section,
              content: parseMarkdownString(section.content),
            }
          : undefined;
      return {
        ...shipment,
        messaging: shipment.messaging
          ? {
              ...shipment.messaging,
              template: {
                ...shipment.messaging.template,
                importantText: parseBaseContentSection(shipment.messaging.template.importantText),
                footer: parseBaseContentSection(shipment.messaging.template.footer),
                confirmationText: parseBaseContentSection(shipment.messaging.template.confirmationText),
                alert: parseBaseContentSection(shipment.messaging.template.alert) as BaseContentSection & AlertProps,
                headerAlert: parseBaseContentSection(shipment.messaging.template.headerAlert) as BaseContentSection &
                  AlertProps,
                importantTextHistory: parseBaseContentSection(shipment.messaging.template.importantTextHistory),
              },
            }
          : undefined,
      };
    };
    return {
      ...order,
      deliveries: order.deliveries?.map(parseShipment),
      collections: order.collections?.map(parseShipment),
      digitalDeliveries: order.digitalDeliveries?.map(parseShipment),
    };
  };

  static toMarketplaceSellerCalloutProps = (
    lineItem: ShipmentLineItem
  ): Pick<MarketplaceSellerCalloutProps, 'marketplaceCallout' | 'sellerName'> | undefined => {
    if (!lineItem.sellerName) {
      return undefined;
    }

    return {
      marketplaceCallout: 'Sent by',
      sellerName: lineItem.sellerName,
    };
  };
}
